import { useCallback } from "react";
import classNames from "classnames";
import { omit } from "lodash";
import invariant from "invariant";

import Button from "../Buttons/Button";
import { useCollapseContext } from "./Collapse";

// This component is an accessible collapse switch button that allows expanding or
// collapsing a sibling <CollapseContent> component when clicked.
//
// Note that this component must be used as a child of a <Collapse> component, or it will
// throw an error.
//
const CollapseSwitch = ({
  hideCaret = false,
  // This defines which "hx" level tag we wrap the button into. This is needed for the
  // collapse / accordion to be WCAG/RGAA compliant.
  headingLevel = 2,
  ...props
}) => {
  const { onClick } = props;

  const forwardProps = omit(props, "onClick", "children", "hideCaret", "headingLevel");
  const { collapseId, isExpanded, setIsExpanded } = useCollapseContext();

  const WrappingHeadingTag = `h${headingLevel}`;

  // Ensure that the component was mounted inside a <Collapse> component
  invariant(
    collapseId,
    "The <CollapseSwitch> component must be used as a child of the <Collapse> component"
  );

  const label = props.label || props.children;

  const handleButtonClicked = useCallback(() => {
    const willExpand = !isExpanded;
    setIsExpanded(willExpand);

    if (onClick) onClick();
  }, [isExpanded, setIsExpanded, onClick]);

  const caretIconClassName = classNames("fa", {
    "fa-caret-down": !isExpanded,
    "fa-caret-up": isExpanded
  });

  return (
    <WrappingHeadingTag>
      <Button
        type="button"
        onClick={handleButtonClicked}
        aria-controls={collapseId}
        aria-expanded={isExpanded ? "true" : "false"}
        label={label}
        iconClassName={!hideCaret && caretIconClassName}
        iconPosition="after"
        data-collapse-switch-button
        {...forwardProps}
      />
    </WrappingHeadingTag>
  );
};

export default CollapseSwitch;
